import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@kognitos/lattice';
import { hasEmptyKeyValueCredentialValues } from './CredentialKeyValueInput';

export interface IEmptyCredentialValuesConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Asks the user to confirm saving key/value credentials that have names but
 * no values, since those are stored as empty secrets.
 *
 * @param props - Component props
 * @returns The confirmation dialog
 */
export function EmptyCredentialValuesConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
}: IEmptyCredentialValuesConfirmDialogProps) {
  const { t: tCommon } = useTranslation('common');
  const { t: tIntegrations } = useTranslation('integrations');

  return (
    <AlertDialog open={isOpen} onOpenChange={open => !open && onCancel()}>
      <AlertDialogContent data-testid="kv-empty-value-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {tIntegrations('integrations.customCredentials.kvEmptyValueTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {tIntegrations(
              'integrations.customCredentials.kvEmptyValueWarning',
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel type="button">
            {tCommon('common.action.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            type="button"
            // `preventDefault` stops Radix from closing the dialog through
            // `onOpenChange`, which would report the confirmation as a cancel.
            // `onConfirm` closes it by clearing `isOpen` instead.
            onClick={event => {
              event.preventDefault();
              onConfirm();
            }}
            data-testid="kv-empty-value-confirm"
          >
            {tIntegrations(
              'integrations.customCredentials.kvEmptyValueConfirm',
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Gates a submit behind {@link EmptyCredentialValuesConfirmDialog} when the
 * submitted key/value credentials contain empty values.
 *
 * @returns `confirmEmptyValues`, which runs `proceed` immediately when there is
 * nothing to confirm and otherwise once the user confirms, and the props for
 * the dialog to render
 */
export const useEmptyCredentialValuesConfirmation = () => {
  const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null);

  const confirmEmptyValues = (values: unknown[], proceed: () => void) => {
    if (hasEmptyKeyValueCredentialValues(values)) {
      setPendingSubmit(() => proceed);
      return;
    }
    proceed();
  };

  const dialogProps: IEmptyCredentialValuesConfirmDialogProps = {
    isOpen: pendingSubmit !== null,
    onConfirm: () => {
      const submit = pendingSubmit;
      setPendingSubmit(null);
      submit?.();
    },
    onCancel: () => setPendingSubmit(null),
  };

  return { confirmEmptyValues, dialogProps };
};
